import type { ActorId } from './temporal';
import type { Cell, Dir4 } from './numeric';

/**
 * EntityId — stable string id authored in level data (e.g. "plate-a", "door-1"). Unique per level.
 * Entities are never created or destroyed at runtime (use state flags instead), so the set of ids is fixed
 * for a level and the engine processes them in ascending code-unit order (`a < b`, never localeCompare).
 */
export type EntityId = string;

/**
 * The 12 core puzzle component kinds from the brief (frozen; the order is part of the contract). L10 "The Core" uses
 * every one of them.
 */
export const CORE_ENTITY_KINDS = Object.freeze([
  'pressurePlate',
  'door',
  'timedSwitch',
  'crate',
  'energyCell',
  'energySocket',
  'elevator',
  'laserEmitter',
  'laserReceiver',
  'temporalGate',
  'movingPlatform',
  'exitPortal',
] as const);
export type CoreEntityKind = (typeof CORE_ENTITY_KINDS)[number];

/**
 * All puzzle component kinds: the core 12, then round-6 expansion kinds APPENDED in order (never reorder, never
 * insert): checkValve (one-way ratchet doorway), chronoRelay (delayed signal line).
 */
export const ENTITY_KINDS = [...CORE_ENTITY_KINDS, 'checkValve', 'chronoRelay'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

export function isEntityKind(v: unknown): v is EntityKind {
  return typeof v === 'string' && (ENTITY_KINDS as readonly string[]).includes(v);
}

/** How a consumer combines multiple wired inputs. */
export type SignalLogic = 'and' | 'or';

/**
 * (round 6, Attuned Devices) Which actors a device answers to. 'player' = the live actor (actor.kind === 'player'),
 * 'echoes' = every echo including nested-loop copies (actor.kind === 'echo'), 'all' = anyone (default).
 */
export type ActorClass = 'all' | 'echoes' | 'player';

/* ------------------------------------------------------------------------------------------------
 * Per-kind CONFIG (static, authored in level data, never mutated) and STATE (dynamic, lives in SimState,
 * integers/booleans/strings/null only). Durations are ticks, distances are units, heights are levels unless
 * suffixed otherwise. Gameplay engineers may ADD optional fields; never repurpose existing ones.
 * ---------------------------------------------------------------------------------------------- */

export interface PressurePlateConfig {
  /** Do non-carried crates press the plate? default true */
  acceptsCrates?: boolean;
  /** Occupant count needed (actors + crates). default 1 */
  threshold?: number;
  /** Once pressed, stays pressed for the rest of the pass. default false */
  latch?: boolean;
  /**
   * (round 6, Attuned Devices) Gold seal ('player') / echo plate ('echoes'). When set to anything but 'all', only
   * matching actors count as occupants (others are ignored completely) and crates/cells NEVER count (acceptsCrates
   * is ignored); `threshold` counts matching bodies. default 'all' (unchanged behaviour).
   */
  accepts?: ActorClass;
  /**
   * (round 6, Paradox Glass) The first time an actor of this class stands on the plate it shatters for the rest of
   * the pass: output false, latch ignored. Must differ from `accepts`.
   */
  shatterOn?: Exclude<ActorClass, 'all'>;
}
export interface PressurePlateState {
  pressed: boolean;
  /** Number of actors + crates currently on the plate. */
  occupants: number;
  /** Consecutive ticks pressed (0 when up). */
  pressedTicks: number;
  /** (round 6, Paradox Glass) Present only when config.shatterOn is set: shattered this pass. */
  broken?: boolean;
}

export interface DoorConfig {
  /** Open when not powered (inverted door). default false */
  startsOpen?: boolean;
  /** Ticks to fully open/close. default 18 */
  openTicks?: number;
}
export interface DoorState {
  /** Desired state from signals (true = wants open). */
  open: boolean;
  /** 0 = closed, 1000 = fully open. Passable (non-solid) when progress >= DOOR_PASSABLE_PROGRESS. */
  progress: number;
  /** True while it wants to close but an actor/crate occupies the doorway (door holds open). */
  blocked: boolean;
}

export interface TimedSwitchConfig {
  /** How long the switch stays on after activation. */
  durationTicks: number;
  /** Re-activating while on restarts the timer. default true */
  retrigger?: boolean;
  /**
   * (level-design addition) Ticks the switch stays locked after switching off; presses fail with reason
   * 'cooldown' meanwhile, so a switch can never be held on continuously by repeated presses. default 0
   */
  cooldownTicks?: number;
  /**
   * (round 6, Attuned Devices) Gold console ('player') / Echo Console ('echoes'). A press by a non-matching actor
   * fails with reason 'attuned' and changes nothing. default 'all'.
   */
  accepts?: ActorClass;
  /**
   * (round 6, Paradox Glass) A press by an actor of this class fails with reason 'shattered' and breaks the console
   * for the rest of the pass (every later press fails 'shattered'). Must differ from `accepts`.
   */
  shatterOn?: Exclude<ActorClass, 'all'>;
}
export interface TimedSwitchState {
  on: boolean;
  remainingTicks: number;
  activatedBy: ActorId | null;
  /** (level-design addition) Remaining cooldown ticks after switching off (only present with cooldownTicks). */
  cooldown?: number;
  /** (round 6, Paradox Glass) Present only when config.shatterOn is set: shattered this pass. */
  broken?: boolean;
}

export type CrateConfig = Record<string, never>;
export interface CrateState {
  /** Ticks since last moved (pickup/drop), cosmetic settle timing for renderers. */
  restTicks: number;
}

export interface EnergyCellConfig {
  /** Optional color/channel tag that sockets may require. */
  tag?: string;
}
export interface EnergyCellState {
  /** Socket currently holding this cell, or null. */
  socketId: EntityId | null;
}

export interface EnergySocketConfig {
  /** Energy cell placed in the socket at tick 0. */
  initialCell?: EntityId;
  /** Only accept cells with this tag. */
  requiresTag?: string;
  /** Can an actor take the cell back out? default true */
  allowExtract?: boolean;
}
export interface EnergySocketState {
  cellId: EntityId | null;
  powered: boolean;
}

export interface ElevatorConfig {
  /** Height levels the platform travels between (ascending). Index 0 is the start unless startIndex set. */
  levels: number[];
  startIndex?: number;
  /** Ticks to travel one height level. default 60 */
  ticksPerLevel?: number;
  /**
   * 'signal': goes to levels[last] while wired input is active, levels[0] otherwise.
   * 'cycle' : interacting (E) on the platform/console advances to the next level.
   * 'ride'  : (gameplay addition) standing on the platform for `rideDelayTicks` sends it to the next level.
   * In 'cycle'/'ride' mode a wired input acts as a call button: each rising edge advances to the next level.
   */
  mode?: 'signal' | 'cycle' | 'ride';
  /** 'ride' mode: ticks an occupant must stand on the resting platform before it departs. default 30 */
  rideDelayTicks?: number;
  /** 'ride'/'cycle' mode: return to startIndex after this many empty ticks away from it. default: never */
  returnDelayTicks?: number;
}
export interface ElevatorState {
  /** Current platform surface height in UNITS. */
  y: number;
  targetIndex: number;
  moving: boolean;
  /** Units moved this tick (for carrying actors / renderers). */
  dy: number;
  /** (gameplay addition) ride/return dwell counter in ticks. */
  dwellTicks?: number;
  /** (gameplay addition) ride mode: false after a ride until the platform has been vacated. */
  armed?: boolean;
  /** (gameplay addition) previous combined input value, for rising-edge call buttons. */
  lastInput?: boolean;
  /** (gameplay addition) true while a departure is held because an actor straddles the shaft edge. */
  held?: boolean;
}

export interface LaserEmitterConfig {
  /** Emits when no input wired; with inputs, emits while the combined signal is active. */
  startsOn?: boolean;
  /** Max beam length in tiles. default 32 */
  rangeTiles?: number;
}
export interface LaserEmitterState {
  on: boolean;
  /** Beam length in units from emitter cell centre (0 when off). */
  beamLength: number;
  /** Entity the beam terminates on (receiver, crate, door...) or null (wall). */
  hitEntityId: EntityId | null;
  /** Actor blocking the beam, if any. */
  hitActorId: ActorId | null;
}

export type LaserReceiverConfig = Record<string, never>;
export interface LaserReceiverState {
  lit: boolean;
  litBy: EntityId | null;
  litTicks: number;
}

export interface TemporalGateConfig {
  /** Who may pass while the gate is closed to others. */
  allows: 'echoes' | 'player' | 'all';
  /** With wired inputs, the gate only lets `allows` pass while powered (else blocks everyone). */
  requiresSignal?: boolean;
}
export interface TemporalGateState {
  open: boolean;
  /** Ticks since last state change (renderer pulse). */
  phaseTicks: number;
  /**
   * Round 4 (additive, feedback only): true while the gate is held open by an occupant although its signal says
   * close (QA-2). Drives the one-shot `gateBlocked` event and the renderer's blocked lamp. NOT hashed
   * (temporalGate.serialize strips it) and never read by any gameplay decision.
   */
  held?: boolean;
}

export interface MovingPlatformConfig {
  /** Waypoint cells the platform travels through (first = start). Same height level. */
  path: Cell[];
  /** Units per tick. default 20 */
  speed?: number;
  /** Ticks to wait at each end/waypoint. default 30 */
  pauseTicks?: number;
  mode?: 'pingpong' | 'loop';
  /** With wired inputs, only moves while powered. */
  requiresSignal?: boolean;
}
export interface MovingPlatformState {
  /** Index of the waypoint being travelled toward. */
  targetIndex: number;
  direction: 1 | -1;
  waitTicks: number;
  /** Planar delta applied this tick (units). Actors standing on it are carried by this delta. */
  dx: number;
  dz: number;
}

export interface ExitPortalConfig {
  /** Optional: label of required objective for UI. */
  objective?: string;
}
export interface ExitPortalState {
  /** Powered/open (all wired inputs satisfied, or no inputs). */
  active: boolean;
  activeTicks: number;
}

/**
 * (round 6) CHECK VALVE — a one-way ratchet doorway across one corridor cell. `def.facing` is the direction of travel
 * that may pass. Solidity per actor: see gameplay/entities/checkValve.ts and contracts.md §5 "Check valve".
 */
export interface CheckValveConfig {
  /** With wired inputs: the allowed direction reverses while the combined input is powered (1 tick latency). */
  reverseWhenPowered?: boolean;
}
export interface CheckValveState {
  /** Allowed direction of travel right now. */
  dir: Dir4;
  /** Ticks since the last direction change (renderer sweep), saturating at 600. */
  phaseTicks: number;
}

/**
 * (round 6) CHRONO RELAY — a delayed signal line ("Echo Line"). Replays its combined input waveform exactly
 * `delayTicks` later (plus the usual 1-tick hop). See contracts.md §9 "Chrono relay".
 */
export interface ChronoRelayConfig {
  /** Delay in ticks, integer in [30, 900]. */
  delayTicks: number;
  /** Output (and assumed input history) at tick 0. default false */
  initialOn?: boolean;
}
export interface ChronoRelayState {
  /** Current output. */
  out: boolean;
  /** Last input value seen (edge detector). */
  last: boolean;
  /** Countdown (ticks) of each in-flight edge, strictly ascending; each one toggles `out` when it reaches 0. */
  pending: number[];
}

export interface EntityConfigMap {
  pressurePlate: PressurePlateConfig;
  door: DoorConfig;
  timedSwitch: TimedSwitchConfig;
  crate: CrateConfig;
  energyCell: EnergyCellConfig;
  energySocket: EnergySocketConfig;
  elevator: ElevatorConfig;
  laserEmitter: LaserEmitterConfig;
  laserReceiver: LaserReceiverConfig;
  temporalGate: TemporalGateConfig;
  movingPlatform: MovingPlatformConfig;
  exitPortal: ExitPortalConfig;
  checkValve: CheckValveConfig;
  chronoRelay: ChronoRelayConfig;
}

export interface EntityStateMap {
  pressurePlate: PressurePlateState;
  door: DoorState;
  timedSwitch: TimedSwitchState;
  crate: CrateState;
  energyCell: EnergyCellState;
  energySocket: EnergySocketState;
  elevator: ElevatorState;
  laserEmitter: LaserEmitterState;
  laserReceiver: LaserReceiverState;
  temporalGate: TemporalGateState;
  movingPlatform: MovingPlatformState;
  exitPortal: ExitPortalState;
  checkValve: CheckValveState;
  chronoRelay: ChronoRelayState;
}

/**
 * Runtime entity record inside SimState. Position is the entity's anchor in units (cell centre for
 * cell-anchored entities; y = surface/base height). Carried entities follow their carrier.
 */
export interface SimEntity<K extends EntityKind = EntityKind> {
  id: EntityId;
  kind: K;
  x: number;
  y: number;
  z: number;
  /** Exclusive ownership: the actor carrying this entity, or null. Mirrors ActorState.carrying. */
  carriedBy: ActorId | null;
  state: EntityStateMap[K];
}
export type AnySimEntity = { [K in EntityKind]: SimEntity<K> }[EntityKind];

/** Level-data definition of one entity. */
export interface EntityDefinitionOf<K extends EntityKind> {
  id: EntityId;
  kind: K;
  col: number;
  row: number;
  /** Height level; defaults to the tile's height level. */
  level?: number;
  /** Orientation (door axis, laser direction, ...). default 0 (north) */
  facing?: Dir4;
  /** Combination of wired inputs. default 'and' */
  logic?: SignalLogic;
  /** Display label for UI/timeline ("Plate A"). */
  label?: string;
  config: EntityConfigMap[K];
}
export type EntityDefinition = { [K in EntityKind]: EntityDefinitionOf<K> }[EntityKind];

/* Shared tuning constants for entity behaviours (sim-side, integers). */
export const DOOR_PASSABLE_PROGRESS = 800;
export const DOOR_DEFAULT_OPEN_TICKS = 18;
/** (round 6) Chrono relay delay bounds (ticks). */
export const CHRONO_RELAY_MIN_DELAY = 30;
export const CHRONO_RELAY_MAX_DELAY = 900;
/** (round 6) Check valve phase counter cap. */
export const CHECK_VALVE_PHASE_CAP = 600;
